import React from 'react';
import type { CardDensity } from '@troia-v3/types';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { Tooltip } from '@/shared/components/ui';
import { Icon, IntegrationIcon } from '@/shared/icons';
import { formatRelativeTime, type RelativeTimeStyle } from '../../utils/time';

/* ---------------------------------------------------------------------------
   Casca — a base `.c3-card` da fundação (cards-v3.css:352–373):
   284px de largura útil, branco, borda fio-de-cabelo, raio 12, padding 14,
   SEM sombra, coluna flex. `gap` (espaço INTERNO entre os blocos do card — ver
   a nota na prop) é a única medida que varia por densidade.
   `overflow: hidden` existe para a seção sangrada do card de Detalhes: ela
   estoura o padding com margem negativa e precisa ser recortada pelo raio do
   card. Não clipa o selo de não lidas do card Normal — ele fica em y=6 e
   x=50 dentro da caixa (conta no Passo 5).
   NÃO declarar margem: o passo entre cards é `--kanban-card-gap` (Tarefa 6).
--------------------------------------------------------------------------- */
export interface ConversationCardShellProps {
  conversationId: string;
  density: CardDensity;
  /**
   * Espaço entre os BLOCOS DENTRO do card (px). ATENÇÃO: não confundir com
   * `DENSITY_TOKENS[d].gap` / `--kanban-card-gap`, que é o passo ENTRE cards e
   * é publicado pela coluna (Tarefa 6). São duas medidas diferentes, com donos
   * diferentes: o card nunca lê nem declara o passo entre cards.
   */
  gap: number;
  onClick: () => void;
  children: React.ReactNode;
}

export const ConversationCardShell: React.FC<ConversationCardShellProps> = ({
  conversationId,
  density,
  gap,
  onClick,
  children,
}) => (
  <div
    data-testid={`attendance-card-${conversationId}`}
    data-card-density={density}
    className="relative flex flex-col min-w-0 cursor-pointer"
    style={{
      gap,
      padding: 14,
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: 'var(--theme-card-background)',
      border: '1px solid var(--theme-card-border)',
      color: 'var(--theme-card-text)',
      transition: 'border-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    }}
    onClick={onClick}
    // Regra dura 1 da fundação: no hover a BORDA escurece. Sem sombra, sem
    // translate — o board é virtualizado e remede altura a cada movimento.
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--theme-input-border)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--theme-card-border)'; }}
  >
    {children}
  </div>
);

/* ---------------------------------------------------------------------------
   Avatares. Três tamanhos permitidos (20/24/28) com as iniciais acompanhando
   (9/10/11) — cards-v3.css:648–673. Squircle em pessoa é decisão travada da
   auditoria de 25/07 (F83). O rosto sem foto é slate, que é o padrão do
   `AvatarFallback` shared — não repintar por fora.
--------------------------------------------------------------------------- */
const INITIAL_FONT_SIZE: Record<20 | 24 | 28, number> = { 20: 9, 24: 10, 28: 11 };

const initialOf = (name: string | undefined): string =>
  name?.trim().charAt(0).toUpperCase() || '?';

export interface PersonAvatarProps {
  name?: string;
  picture?: string;
  size: 20 | 24 | 28;
}

/** Rosto do CONTATO. Nunca leva a marca de papel. */
export const ContactAvatar: React.FC<PersonAvatarProps> = ({ name, picture, size }) => (
  <Avatar className="flex-none" style={{ width: size, height: size }} isSquircle>
    <AvatarImage src={picture} alt={name ?? 'Contato'} isSquircle />
    <AvatarFallback isSquircle>
      <span className="font-semibold" style={{ fontSize: INITIAL_FONT_SIZE[size] }}>
        {initialOf(name)}
      </span>
    </AvatarFallback>
  </Avatar>
);

/**
 * Rosto do RESPONSÁVEL. Duas diferenças em relação ao do contato:
 * - marca de papel: anel fino por fora (`.c3-avatar--owner`, cards-v3.css:682),
 *   para que os dois rostos nunca apareçam sem nenhuma distinção — é o que a
 *   fundação exige quando o card não traz rótulo textual de papel (o Compacto
 *   é justamente esse caso);
 * - estado vazio: sem responsável, contorno tracejado, ícone de pessoa
 *   (`.c3-avatar--empty`, cards-v3.css:698–702) e `Tooltip` shared — "ninguém
 *   responde" tem desenho próprio, não é rosto ausente. Esse vazio é IDÊNTICO
 *   nos três módulos (CRM, Atendimento e Helpdesk): mesmo tracejado, mesmo
 *   ícone `user`, mesmo `Tooltip` — nunca `title` nativo.
 */
export const AssigneeAvatar: React.FC<PersonAvatarProps> = ({ name, picture, size }) => {
  if (!name) {
    return (
      <Tooltip content="Sem responsável">
        <span
          className="inline-flex items-center justify-center flex-none"
          style={{
            width: size,
            height: size,
            borderRadius: '32%',
            border: '1px dashed var(--theme-input-border)',
            color: 'var(--theme-muted-text)',
          }}
          aria-label="Sem responsável"
        >
          <Icon name="user" size={size === 20 ? 11 : 13} />
        </span>
      </Tooltip>
    );
  }
  return (
    <span
      className="inline-flex flex-none"
      style={{ outline: '1px solid var(--theme-input-border)', outlineOffset: 2, borderRadius: '32%' }}
    >
      <ContactAvatar name={name} picture={picture} size={size} />
    </span>
  );
};

/* ---------------------------------------------------------------------------
   Selo de não lidas — NEUTRO por decisão (seção 10 do desenho).
   Significa "não visto por mim", não "cliente esperando": incrementa para todos
   os usuários da empresa exceto o remetente, inclusive em mensagem que NÓS
   enviamos e em resposta da IA. Por isso não é vermelho e não fica colado no
   nome. Cinza cheio com texto invertido é legível nos dois temas:
   claro  → fundo #6b7280 + texto #ffffff
   escuro → fundo #9ca3af + texto #1e293b
   (`--theme-muted-text` e `--theme-button-primary-text` em cssVariableMapper.ts:95 e :79)
--------------------------------------------------------------------------- */
export interface UnreadBadgeProps {
  count: number;
  /** `floating` = sobre o canto do avatar (card Normal); `inline` = na linha. */
  variant: 'floating' | 'inline';
  'data-testid'?: string;
}

export const UnreadBadge: React.FC<UnreadBadgeProps> = ({ count, variant, 'data-testid': testId }) => {
  if (count <= 0) return null;
  const floating = variant === 'floating';
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center justify-center flex-none"
      style={{
        ...(floating
          ? { position: 'absolute', top: -8, right: -8, boxShadow: '0 0 0 2px var(--theme-card-background)' }
          : {}),
        minWidth: floating ? 18 : 20,
        height: floating ? 18 : 20,
        padding: floating ? '0 5px' : '0 6px',
        borderRadius: 9999,
        backgroundColor: 'var(--theme-muted-text)',
        color: 'var(--theme-button-primary-text)',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
      aria-label={`${count} mensagens não vistas por você`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

/* ---------------------------------------------------------------------------
   Meta de canal e meta de tempo — `.c3-meta` (cards-v3.css:462–472):
   12px muted, gap 4, tabular, o texto trunca e o ícone nunca encolhe.
--------------------------------------------------------------------------- */
export interface ChannelMetaProps {
  providerId?: string;
  label?: string;
}

export const ChannelMeta: React.FC<ChannelMetaProps> = ({ providerId, label }) => (
  <span
    className="inline-flex items-center gap-1 min-w-0"
    style={{ fontSize: 12, color: 'var(--theme-muted-text)' }}
  >
    {providerId ? (
      <span className="flex-none inline-flex items-center">
        <IntegrationIcon id={providerId} size={14} variant="brand" />
      </span>
    ) : (
      <Icon name="message-circle" size={14} />
    )}
    <span className="truncate min-w-0">{label ?? 'Canal'}</span>
  </span>
);

export interface RelativeTimeProps {
  iso?: string;
  style?: RelativeTimeStyle;
  /** Cor do texto+ícone — o nível do esfriamento pinta o tempo (padrão: muted). */
  color?: string;
}

export const RelativeTime: React.FC<RelativeTimeProps> = ({ iso, style = 'short', color }) => (
  <span
    className="inline-flex items-center gap-1 flex-none"
    style={{ fontSize: 12, color: color ?? 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
  >
    <Icon name="clock" size={14} />
    {formatRelativeTime(iso, style)}
  </span>
);

/* ---------------------------------------------------------------------------
   Trilho do esfriamento — a anatomia EXATA da barra do card 06 aprovado
   (comparacao.html:1701–1716 · CSS :634–655): 3px, raio total, fundo
   `--theme-surface-sunken`, preenchimento pela razão decorrido/alvo na cor
   sólida do nível. Extraída do Compacto quando a barra foi levada às outras
   densidades (decisão do dono, 16/08): com o SLA real do Plano D, a barra é o
   mecanismo de leitura da coluna — o tempo sem cor não se identifica.
--------------------------------------------------------------------------- */
export interface CooldownTrackProps {
  ratio: number;
  /** Cor sólida do nível (COOLDOWN_COLORS[level].solid). */
  color: string;
}

export const CooldownTrack: React.FC<CooldownTrackProps> = ({ ratio, color }) => (
  <div
    className="flex-1 min-w-0"
    style={{ height: 3, borderRadius: 9999, overflow: 'hidden', backgroundColor: 'var(--theme-surface-sunken)' }}
  >
    <div
      style={{
        height: '100%',
        borderRadius: 'inherit',
        width: `${Math.round(ratio * 100)}%`,
        backgroundColor: color,
        transition: 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    />
  </div>
);

/* Fio interno — `.c3-sep` (cards-v3.css:511–516): 1px, respiro de 4px em cima
   e embaixo (que se soma ao gap do card). */
export const CardSeparator: React.FC = () => (
  <div style={{ height: 1, margin: '4px 0', backgroundColor: 'var(--theme-card-border)' }} />
);
